import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Archive, Lock, Home, Building2, Briefcase, Trash2, MapPin, Calendar,
  Copy, CheckCircle2, Clock, AlertCircle, ChevronDown, Plus, X, Shield,
  Save, FileText, Heart, Baby, Skull, DollarSign, Globe, Stethoscope,
  RefreshCw, Landmark, Eye, BookOpen, ArrowRight
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import Toggle from "../ui/Toggle";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Props {
  matterId: string;
  caseData: any;
  onUpdate: () => void;
}

interface CopyRecord {
  recipientName: string;
  recipientRole: string;
  dateProvided: string;
  format: string;
  acknowledged: string;
  acknowledgedDate: string | null;
  notes: string | null;
}

interface LifeEvent {
  eventType: string;
  eventDate: string;
  description: string | null;
  triggeredReview: boolean;
  loggedBy: string | null;
  loggedAt: string;
}

interface Review {
  date: string;
  reviewedBy: string;
  outcome: string | null;
  notes: string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const LOCATION_TYPES = [
  { value: "firm_vault", label: "Firm Vault", Icon: Lock, color: "emerald", bgColor: "bg-emerald-50", textColor: "text-emerald-600", borderColor: "border-emerald-200" },
  { value: "client_possession", label: "Client Possession", Icon: Home, color: "blue", bgColor: "bg-blue-50", textColor: "text-blue-600", borderColor: "border-blue-200" },
  { value: "court_filing", label: "Court Filing", Icon: Building2, color: "purple", bgColor: "bg-purple-50", textColor: "text-purple-600", borderColor: "border-purple-200" },
  { value: "other_attorney", label: "Other Attorney", Icon: Briefcase, color: "amber", bgColor: "bg-amber-50", textColor: "text-amber-600", borderColor: "border-amber-200" },
  { value: "destroyed_revoked", label: "Destroyed / Revoked", Icon: Trash2, color: "red", bgColor: "bg-red-50", textColor: "text-red-600", borderColor: "border-red-200" },
];

const STORAGE_METHODS = [
  { value: "home_safe", label: "Home Safe" },
  { value: "bank_safe_deposit", label: "Bank Safe Deposit Box" },
  { value: "filing_cabinet", label: "Filing Cabinet" },
  { value: "lockbox", label: "Lockbox" },
  { value: "unknown", label: "Unknown" },
  { value: "other", label: "Other" },
];

const COPY_ROLES = [
  { value: "executor", label: "Executor" },
  { value: "spouse", label: "Spouse" },
  { value: "trustee", label: "Trustee" },
  { value: "beneficiary", label: "Beneficiary" },
  { value: "financial_advisor", label: "Financial Advisor" },
  { value: "accountant", label: "Accountant" },
  { value: "attorney", label: "Attorney" },
  { value: "other", label: "Other" },
];

const COPY_FORMATS = [
  { value: "physical", label: "Physical" },
  { value: "digital", label: "Digital" },
  { value: "certified", label: "Certified" },
];

const ACK_STATUSES = [
  { value: "yes", label: "Acknowledged" },
  { value: "pending", label: "Pending" },
  { value: "not_requested", label: "Not Requested" },
];

const RETENTION_CLASSES = [
  { value: "permanent", label: "Permanent", color: "bg-emerald-100 text-emerald-700" },
  { value: "standard", label: "Standard", color: "bg-blue-100 text-blue-700" },
  { value: "custom", label: "Custom", color: "bg-amber-100 text-amber-700" },
];

const LIFE_EVENT_TYPES = [
  { value: "Marriage", label: "Marriage", Icon: Heart },
  { value: "Divorce", label: "Divorce", Icon: X },
  { value: "Birth of Child", label: "Birth of Child", Icon: Baby },
  { value: "Death of Beneficiary", label: "Death of Beneficiary", Icon: Skull },
  { value: "Major Asset Change", label: "Major Asset Change", Icon: DollarSign },
  { value: "Relocation", label: "Relocation", Icon: Globe },
  { value: "Health Change", label: "Health Change", Icon: Stethoscope },
  { value: "Retirement", label: "Retirement", Icon: RefreshCw },
  { value: "Business Change", label: "Business Change", Icon: Landmark },
  { value: "Other", label: "Other", Icon: FileText },
];

const emptyCopy: CopyRecord = {
  recipientName: "", recipientRole: "", dateProvided: new Date().toISOString().split("T")[0],
  format: "physical", acknowledged: "not_requested", acknowledgedDate: null, notes: null,
};

const emptyLifeEvent: LifeEvent = {
  eventType: "", eventDate: new Date().toISOString().split("T")[0],
  description: null, triggeredReview: false, loggedBy: null, loggedAt: new Date().toISOString(),
};

const emptyReview: Review = {
  date: new Date().toISOString().split("T")[0], reviewedBy: "",
  outcome: null, notes: null,
};

const formatLabel = (s: string) => s?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";
const formatDate = (d: string | Date | null) => {
  if (!d) return "Not set";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const daysBetween = (a: Date, b: Date) => Math.ceil((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));

// ── Component ─────────────────────────────────────────────────────────────────
const EstateCustodyTab: React.FC<Props> = ({ matterId, caseData, onUpdate }) => {
  const custody = caseData?.custody || {};
  const currentLocation = custody.currentLocation || {};
  const copies: CopyRecord[] = custody.copies || [];
  const reviewSchedule = custody.reviewSchedule || {};
  const lifeEvents: LifeEvent[] = custody.lifeEvents || [];
  const reviews: Review[] = custody.reviews || [];
  const retention = custody.retention || {};
  const registry = custody.registry || {};

  // ── State ─────────────────────────────────────────────────────────────────
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    copies: true, schedule: true, lifeEvents: true, retention: true,
  });

  // Transfer custody modal
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTo, setTransferTo] = useState<string | null>(null);
  const [transferFields, setTransferFields] = useState<any>({});
  const [transferReason, setTransferReason] = useState("");

  // Copy modals
  const [showAddCopy, setShowAddCopy] = useState(false);
  const [copyForm, setCopyForm] = useState<CopyRecord>({ ...emptyCopy });

  // Review modal
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState<Review>({ ...emptyReview });

  // Life event modal
  const [showLifeEvent, setShowLifeEvent] = useState(false);
  const [lifeEventForm, setLifeEventForm] = useState<LifeEvent>({ ...emptyLifeEvent });

  // Editable state for inline fields
  const [localSchedule, setLocalSchedule] = useState({
    frequencyYears: reviewSchedule.frequencyYears ?? 3,
    reminderLeadDays: reviewSchedule.reminderLeadDays ?? 60,
    notifyClient: reviewSchedule.notifyClient ?? false,
  });
  const [localRetention, setLocalRetention] = useState({
    retentionClass: retention.retentionClass || "standard",
    legalHold: retention.legalHold || false,
  });
  const [localRegistry, setLocalRegistry] = useState({
    usWillRegistry: {
      registered: registry.usWillRegistry?.registered || false,
      registrationDate: registry.usWillRegistry?.registrationDate ? new Date(registry.usWillRegistry.registrationDate).toISOString().split("T")[0] : "",
      referenceNumber: registry.usWillRegistry?.referenceNumber || "",
    },
    noticeConnect: {
      registered: registry.noticeConnect?.registered || false,
      registrationDate: registry.noticeConnect?.registrationDate ? new Date(registry.noticeConnect.registrationDate).toISOString().split("T")[0] : "",
      referenceNumber: registry.noticeConnect?.referenceNumber || "",
    },
  });
  const [localCopies, setLocalCopies] = useState<CopyRecord[]>(copies);
  const [localReviews, setLocalReviews] = useState<Review[]>(reviews);
  const [localLifeEvents, setLocalLifeEvents] = useState<LifeEvent[]>(lifeEvents);

  // Reset local state when caseData changes
  useEffect(() => {
    const c = caseData?.custody || {};
    const sched = c.reviewSchedule || {};
    const ret = c.retention || {};
    const reg = c.registry || {};
    setLocalSchedule({
      frequencyYears: sched.frequencyYears ?? 3,
      reminderLeadDays: sched.reminderLeadDays ?? 60,
      notifyClient: sched.notifyClient ?? false,
    });
    setLocalRetention({
      retentionClass: ret.retentionClass || "standard",
      legalHold: ret.legalHold || false,
    });
    setLocalRegistry({
      usWillRegistry: {
        registered: reg.usWillRegistry?.registered || false,
        registrationDate: reg.usWillRegistry?.registrationDate ? new Date(reg.usWillRegistry.registrationDate).toISOString().split("T")[0] : "",
        referenceNumber: reg.usWillRegistry?.referenceNumber || "",
      },
      noticeConnect: {
        registered: reg.noticeConnect?.registered || false,
        registrationDate: reg.noticeConnect?.registrationDate ? new Date(reg.noticeConnect.registrationDate).toISOString().split("T")[0] : "",
        referenceNumber: reg.noticeConnect?.referenceNumber || "",
      },
    });
    setLocalCopies(c.copies || []);
    setLocalReviews(c.reviews || []);
    setLocalLifeEvents(c.lifeEvents || []);
    setDirty(false);
  }, [caseData]);

  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const markDirty = () => { if (!dirty) setDirty(true); };

  // ── Location Config Lookup ────────────────────────────────────────────────
  const currentLocConfig = LOCATION_TYPES.find(l => l.value === currentLocation.locationType);

  // ── Review schedule computations ──────────────────────────────────────────
  const nextReviewDate = reviewSchedule.nextReviewDate ? new Date(reviewSchedule.nextReviewDate) : null;
  const daysUntilReview = nextReviewDate ? daysBetween(new Date(), nextReviewDate) : null;
  const isOverdue = daysUntilReview !== null && daysUntilReview < 0;

  // ── Save All ──────────────────────────────────────────────────────────────
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const updatedCustody = {
        ...custody,
        copies: localCopies,
        reviewSchedule: {
          ...reviewSchedule,
          frequencyYears: localSchedule.frequencyYears,
          reminderLeadDays: localSchedule.reminderLeadDays,
          notifyClient: localSchedule.notifyClient,
        },
        lifeEvents: localLifeEvents,
        reviews: localReviews,
        retention: localRetention,
        registry: {
          usWillRegistry: {
            registered: localRegistry.usWillRegistry.registered,
            registrationDate: localRegistry.usWillRegistry.registrationDate || null,
            referenceNumber: localRegistry.usWillRegistry.referenceNumber || null,
          },
          noticeConnect: {
            registered: localRegistry.noticeConnect.registered,
            registrationDate: localRegistry.noticeConnect.registrationDate || null,
            referenceNumber: localRegistry.noticeConnect.referenceNumber || null,
          },
        },
      };
      await estatesApi.update(caseData._id, { custody: updatedCustody });
      setDirty(false);
      onUpdate();
    } catch (err) {
      console.error("Failed to save custody data:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Transfer Custody ──────────────────────────────────────────────────────
  const handleTransfer = async () => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const newLocation: any = {
        locationType: transferTo,
        since: new Date().toISOString(),
        ...transferFields,
      };
      const updatedCustody = { ...custody, currentLocation: newLocation };
      await estatesApi.update(caseData._id, { custody: updatedCustody });
      setShowTransfer(false);
      setTransferTo(null);
      setTransferFields({});
      setTransferReason("");
      onUpdate();
    } catch (err) {
      console.error("Failed to transfer custody:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Add Copy ──────────────────────────────────────────────────────────────
  const handleAddCopy = () => {
    setLocalCopies(prev => [...prev, { ...copyForm }]);
    setShowAddCopy(false);
    setCopyForm({ ...emptyCopy });
    markDirty();
  };

  // ── Quick Acknowledge ─────────────────────────────────────────────────────
  const handleAcknowledge = (idx: number) => {
    setLocalCopies(prev => prev.map((c, i) =>
      i === idx ? { ...c, acknowledged: "yes", acknowledgedDate: new Date().toISOString() } : c
    ));
    markDirty();
  };

  // ── Add Review ────────────────────────────────────────────────────────────
  const handleAddReview = () => {
    setLocalReviews(prev => [...prev, { ...reviewForm }]);
    setShowReview(false);
    setReviewForm({ ...emptyReview });
    markDirty();
  };

  // ── Add Life Event ────────────────────────────────────────────────────────
  const handleAddLifeEvent = () => {
    setLocalLifeEvents(prev => [...prev, { ...lifeEventForm, loggedAt: new Date().toISOString() }]);
    setShowLifeEvent(false);
    setLifeEventForm({ ...emptyLifeEvent });
    markDirty();
  };

  // ── Input class helper ────────────────────────────────────────────────────
  const inputClass = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* ── Header Card ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Archive className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Document Custody & Storage</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-sm text-slate-500">Current location:</p>
                {currentLocConfig ? (
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${currentLocConfig.bgColor} ${currentLocConfig.textColor}`}>
                    <currentLocConfig.Icon className="h-3 w-3" />
                    {currentLocConfig.label}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                    <AlertCircle className="h-3 w-3" /> Not assigned
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowTransfer(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
          >
            <ArrowRight className="h-4 w-4" /> Transfer Custody
          </button>
        </div>
      </div>

      {/* ── Current Location Card ──────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <div className={`bg-white rounded-xl shadow-sm border overflow-hidden ${currentLocConfig ? currentLocConfig.borderColor : "border-slate-200"}`}>
          <div className={`px-6 py-4 ${currentLocConfig ? currentLocConfig.bgColor : "bg-slate-50"}`}>
            <div className="flex items-center gap-3">
              {currentLocConfig ? (
                <currentLocConfig.Icon className={`h-6 w-6 ${currentLocConfig.textColor}`} />
              ) : (
                <AlertCircle className="h-6 w-6 text-slate-400" />
              )}
              <div>
                <h3 className="font-semibold text-slate-800">
                  {currentLocConfig ? currentLocConfig.label : "No Location Assigned"}
                </h3>
                {currentLocation.since && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Since {formatDate(currentLocation.since)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            {!currentLocation.locationType && (
              <p className="text-sm text-slate-400 text-center py-4">
                No custody location has been assigned. Use "Transfer Custody" to set the initial location.
              </p>
            )}

            {currentLocation.locationType === "firm_vault" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Vault Reference</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.vaultReference || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Shelf Location</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.shelfLocation || "Not specified"}</p>
                </div>
              </div>
            )}

            {currentLocation.locationType === "client_possession" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Storage Method</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.storageMethod ? formatLabel(currentLocation.storageMethod) : "Not specified"}</p>
                </div>
                {currentLocation.storageMethod === "bank_safe_deposit" && (
                  <>
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Institution</p>
                      <p className="text-sm text-slate-700 font-medium">{currentLocation.institution || "Not specified"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Box Number</p>
                      <p className="text-sm text-slate-700 font-medium">{currentLocation.boxNumber || "Not specified"}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {currentLocation.locationType === "court_filing" && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Court Name</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.courtName || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Filing Number</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.filingNumber || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Filing Date</p>
                  <p className="text-sm text-slate-700 font-medium">{formatDate(currentLocation.filingDate)}</p>
                </div>
              </div>
            )}

            {currentLocation.locationType === "other_attorney" && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Firm Name</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.firmName || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Contact Info</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.contactInfo || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">External Reference</p>
                  <p className="text-sm text-slate-700 font-medium">{currentLocation.externalReference || "Not specified"}</p>
                </div>
              </div>
            )}

            {currentLocation.locationType === "destroyed_revoked" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Destruction Date</p>
                  <p className="text-sm text-red-600 font-medium">{formatDate(currentLocation.destructionDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-1">Destruction Method</p>
                  <p className="text-sm text-red-600 font-medium">{currentLocation.destructionMethod || "Not specified"}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* ── Copy Distribution Section ──────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <button onClick={() => toggleSection("copies")} className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-cyan-50 flex items-center justify-center">
              <Copy className="h-4.5 w-4.5 text-cyan-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Copy Distribution</h3>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">{localCopies.length}</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${expandedSections.copies ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {expandedSections.copies && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="px-4 pb-4 space-y-2">
                {localCopies.length === 0 && (
                  <p className="text-sm text-slate-400 py-4 text-center">No copies distributed yet</p>
                )}
                {localCopies.map((c, i) => {
                  const ackIcon = c.acknowledged === "yes" ? CheckCircle2 : c.acknowledged === "pending" ? Clock : AlertCircle;
                  const AckIcon = ackIcon;
                  const ackColor = c.acknowledged === "yes" ? "text-emerald-500" : c.acknowledged === "pending" ? "text-amber-500" : "text-slate-400";
                  const ackBg = c.acknowledged === "yes" ? "bg-emerald-50" : c.acknowledged === "pending" ? "bg-amber-50" : "bg-slate-50";
                  const formatBadge = c.format === "physical" ? "bg-slate-100 text-slate-700" : c.format === "digital" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700";
                  const roleLabel = COPY_ROLES.find(r => r.value === c.recipientRole)?.label || formatLabel(c.recipientRole);

                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                      className="group flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all bg-slate-50/30"
                    >
                      <div className={`h-9 w-9 rounded-full ${ackBg} flex items-center justify-center flex-shrink-0`}>
                        <AckIcon className={`h-4 w-4 ${ackColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800 text-sm">{c.recipientName}</span>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">{roleLabel}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${formatBadge}`}>{formatLabel(c.format)}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(c.dateProvided)}</span>
                          {c.notes && <span className="truncate max-w-[200px]">{c.notes}</span>}
                        </div>
                      </div>
                      {c.acknowledged !== "yes" && (
                        <button onClick={() => handleAcknowledge(i)}
                          className="px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                        >
                          Mark Acknowledged
                        </button>
                      )}
                      {c.acknowledged === "yes" && c.acknowledgedDate && (
                        <span className="text-[11px] text-emerald-500 flex-shrink-0">Ack {formatDate(c.acknowledgedDate)}</span>
                      )}
                    </motion.div>
                  );
                })}
                <button onClick={() => setShowAddCopy(true)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0F5132] py-2 px-3 transition-colors">
                  <Plus className="h-3.5 w-3.5" /> Add Copy Record
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Review Schedule Section ────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className={`bg-white rounded-xl shadow-sm border overflow-hidden ${isOverdue ? "border-red-300" : "border-slate-200"}`}
      >
        <button onClick={() => toggleSection("schedule")} className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-xl ${isOverdue ? "bg-red-50" : "bg-emerald-50"} flex items-center justify-center`}>
              <Calendar className={`h-4.5 w-4.5 ${isOverdue ? "text-red-600" : "text-emerald-600"}`} />
            </div>
            <h3 className="font-semibold text-slate-800">Review Schedule</h3>
            {isOverdue && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 animate-pulse">
                Overdue
              </span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${expandedSections.schedule ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {expandedSections.schedule && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="px-4 pb-4">
                {/* Schedule info cards */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Frequency</p>
                    <p className="text-xl font-bold text-slate-700 mt-1">Every {localSchedule.frequencyYears} yr{localSchedule.frequencyYears !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Last Review</p>
                    <p className="text-sm font-semibold text-slate-700 mt-1">{formatDate(reviewSchedule.lastReviewDate)}</p>
                  </div>
                  <div className={`rounded-xl p-3 text-center ${isOverdue ? "bg-red-50" : "bg-emerald-50"}`}>
                    <p className={`text-xs uppercase font-semibold tracking-wide ${isOverdue ? "text-red-400" : "text-emerald-400"}`}>Next Review</p>
                    <p className={`text-sm font-semibold mt-1 ${isOverdue ? "text-red-700" : "text-emerald-700"}`}>{formatDate(reviewSchedule.nextReviewDate)}</p>
                    {daysUntilReview !== null && (
                      <p className={`text-[11px] mt-0.5 ${isOverdue ? "text-red-500" : "text-emerald-500"}`}>
                        {isOverdue ? `${Math.abs(daysUntilReview)} days overdue` : `${daysUntilReview} days remaining`}
                      </p>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                {daysUntilReview !== null && reviewSchedule.lastReviewDate && !isOverdue && (
                  <div className="mb-4">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, Math.max(0, 100 - (daysUntilReview / (localSchedule.frequencyYears * 365)) * 100))}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Settings row */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Frequency (years)</label>
                    <input type="number" min={1} max={10} value={localSchedule.frequencyYears}
                      onChange={e => { setLocalSchedule(prev => ({ ...prev, frequencyYears: parseInt(e.target.value) || 3 })); markDirty(); }}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Reminder Lead (days)</label>
                    <input type="number" min={0} max={365} value={localSchedule.reminderLeadDays}
                      onChange={e => { setLocalSchedule(prev => ({ ...prev, reminderLeadDays: parseInt(e.target.value) || 60 })); markDirty(); }}
                      className={inputClass}
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={localSchedule.notifyClient}
                        onChange={e => { setLocalSchedule(prev => ({ ...prev, notifyClient: e.target.checked })); markDirty(); }}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      Notify Client
                    </label>
                  </div>
                </div>

                <button onClick={() => setShowReview(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors mb-4"
                >
                  <CheckCircle2 className="h-4 w-4" /> Complete Review
                </button>

                {/* Review history */}
                {localReviews.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Review History</h4>
                    <div className="space-y-2">
                      {localReviews.slice().reverse().map((r, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/30">
                          <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <Eye className="h-4 w-4 text-emerald-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-700">{formatDate(r.date)}</span>
                              {r.outcome && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700">{r.outcome}</span>}
                            </div>
                            {r.notes && <p className="text-xs text-slate-500 mt-1">{r.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Life Events Section ────────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <button onClick={() => toggleSection("lifeEvents")} className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-pink-50 flex items-center justify-center">
              <Heart className="h-4.5 w-4.5 text-pink-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Life Events</h3>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">{localLifeEvents.length}</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${expandedSections.lifeEvents ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {expandedSections.lifeEvents && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="px-4 pb-4">
                {localLifeEvents.length === 0 && (
                  <p className="text-sm text-slate-400 py-4 text-center">No life events recorded</p>
                )}

                {/* Timeline */}
                {localLifeEvents.length > 0 && (
                  <div className="relative">
                    <div className="absolute left-[18px] top-4 bottom-4 w-0.5 bg-slate-200" />
                    <div className="space-y-3">
                      {localLifeEvents.slice().reverse().map((ev, i) => {
                        const evConfig = LIFE_EVENT_TYPES.find(t => t.value === ev.eventType);
                        const EvIcon = evConfig?.Icon || FileText;
                        return (
                          <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                            className="flex items-start gap-3 relative pl-1"
                          >
                            <div className="h-9 w-9 rounded-full bg-white border-2 border-pink-200 flex items-center justify-center flex-shrink-0 z-10">
                              <EvIcon className="h-4 w-4 text-pink-500" />
                            </div>
                            <div className="flex-1 p-3 rounded-xl border border-slate-100 bg-slate-50/30 hover:border-slate-200 transition-all">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">{ev.eventType}</span>
                                <span className="text-xs text-slate-500">{formatDate(ev.eventDate)}</span>
                                {ev.triggeredReview && (
                                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">Triggered Review</span>
                                )}
                              </div>
                              {ev.description && <p className="text-sm text-slate-600 mt-1">{ev.description}</p>}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button onClick={() => setShowLifeEvent(true)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0F5132] py-2 px-3 transition-colors mt-2">
                  <Plus className="h-3.5 w-3.5" /> Add Life Event
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Retention & Registry Section ───────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <button onClick={() => toggleSection("retention")} className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-slate-100 flex items-center justify-center">
              <Shield className="h-4.5 w-4.5 text-slate-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Retention & Registry</h3>
            {localRetention.legalHold && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 animate-pulse">Legal Hold</span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${expandedSections.retention ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {expandedSections.retention && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="px-4 pb-4 space-y-5">
                {/* Retention class */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Retention Class</label>
                  <div className="flex gap-2">
                    {RETENTION_CLASSES.map(rc => (
                      <button key={rc.value}
                        onClick={() => { setLocalRetention(prev => ({ ...prev, retentionClass: rc.value })); markDirty(); }}
                        className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                          localRetention.retentionClass === rc.value
                            ? `${rc.color} border-current ring-2 ring-current/20`
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}
                      >
                        {rc.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Legal hold */}
                <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/30">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${localRetention.legalHold ? "bg-red-100" : "bg-slate-100"}`}>
                      <Lock className={`h-4 w-4 ${localRetention.legalHold ? "text-red-600" : "text-slate-400"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Legal Hold</p>
                      <p className="text-xs text-slate-500">Prevent destruction or disposal of documents</p>
                    </div>
                  </div>
                  <Toggle
                    checked={localRetention.legalHold}
                    onChange={(val) => { setLocalRetention(prev => ({ ...prev, legalHold: val })); markDirty(); }}
                  />
                </div>

                {/* Will Registry */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Will Registry</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* US Will Registry */}
                    <div className={`p-4 rounded-xl border ${localRegistry.usWillRegistry.registered ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50/30"}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <BookOpen className={`h-4 w-4 ${localRegistry.usWillRegistry.registered ? "text-emerald-600" : "text-slate-400"}`} />
                          <span className="text-sm font-medium text-slate-700">US Will Registry</span>
                        </div>
                        {localRegistry.usWillRegistry.registered ? (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700">Registered</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">Not Registered</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={localRegistry.usWillRegistry.registered}
                            onChange={e => { setLocalRegistry(prev => ({ ...prev, usWillRegistry: { ...prev.usWillRegistry, registered: e.target.checked } })); markDirty(); }}
                            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          Registered
                        </label>
                        {localRegistry.usWillRegistry.registered && (
                          <>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Registration Date</label>
                              <input type="date" value={localRegistry.usWillRegistry.registrationDate}
                                onChange={e => { setLocalRegistry(prev => ({ ...prev, usWillRegistry: { ...prev.usWillRegistry, registrationDate: e.target.value } })); markDirty(); }}
                                className={inputClass}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Reference Number</label>
                              <input value={localRegistry.usWillRegistry.referenceNumber}
                                onChange={e => { setLocalRegistry(prev => ({ ...prev, usWillRegistry: { ...prev.usWillRegistry, referenceNumber: e.target.value } })); markDirty(); }}
                                placeholder="e.g., WR-2024-1234"
                                className={inputClass}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* NoticeConnect */}
                    <div className={`p-4 rounded-xl border ${localRegistry.noticeConnect.registered ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50/30"}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Globe className={`h-4 w-4 ${localRegistry.noticeConnect.registered ? "text-emerald-600" : "text-slate-400"}`} />
                          <span className="text-sm font-medium text-slate-700">NoticeConnect</span>
                        </div>
                        {localRegistry.noticeConnect.registered ? (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700">Registered</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">Not Registered</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={localRegistry.noticeConnect.registered}
                            onChange={e => { setLocalRegistry(prev => ({ ...prev, noticeConnect: { ...prev.noticeConnect, registered: e.target.checked } })); markDirty(); }}
                            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          Registered
                        </label>
                        {localRegistry.noticeConnect.registered && (
                          <>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Registration Date</label>
                              <input type="date" value={localRegistry.noticeConnect.registrationDate}
                                onChange={e => { setLocalRegistry(prev => ({ ...prev, noticeConnect: { ...prev.noticeConnect, registrationDate: e.target.value } })); markDirty(); }}
                                className={inputClass}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Reference Number</label>
                              <input value={localRegistry.noticeConnect.referenceNumber}
                                onChange={e => { setLocalRegistry(prev => ({ ...prev, noticeConnect: { ...prev.noticeConnect, referenceNumber: e.target.value } })); markDirty(); }}
                                placeholder="e.g., NC-2024-5678"
                                className={inputClass}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Floating Save Button ───────────────────────────────────────────── */}
      <AnimatePresence>
        {dirty && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-40"
          >
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-[#0F5132] text-white rounded-xl shadow-lg shadow-emerald-900/20 text-sm font-bold hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-xl"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Transfer Custody Modal ─────────────────────────────────────────── */}
      <PremiumModal
        open={showTransfer}
        onClose={() => { setShowTransfer(false); setTransferTo(null); setTransferFields({}); setTransferReason(""); }}
        title="Transfer Custody"
        subtitle="Move the document to a new location"
        icon={<ArrowRight className="h-5 w-5" />}
        size="lg"
      >
        <div className="p-1 space-y-5">
          {/* From (read-only) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Location</label>
            <div className={`px-4 py-3 rounded-xl border ${currentLocConfig ? `${currentLocConfig.bgColor} ${currentLocConfig.borderColor}` : "bg-slate-50 border-slate-200"}`}>
              <div className="flex items-center gap-2">
                {currentLocConfig ? (
                  <>
                    <currentLocConfig.Icon className={`h-4 w-4 ${currentLocConfig.textColor}`} />
                    <span className={`text-sm font-medium ${currentLocConfig.textColor}`}>{currentLocConfig.label}</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-500">Not yet assigned</span>
                  </>
                )}
                {currentLocation.since && (
                  <span className="text-xs text-slate-400 ml-auto">since {formatDate(currentLocation.since)}</span>
                )}
              </div>
            </div>
          </div>

          {/* To (visual cards) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Transfer To</label>
            <div className="grid grid-cols-3 gap-2">
              {LOCATION_TYPES.map(lt => {
                const isSelected = transferTo === lt.value;
                return (
                  <button key={lt.value}
                    onClick={() => { setTransferTo(lt.value); setTransferFields({}); }}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                      isSelected ? `${lt.borderColor} ${lt.bgColor} ring-2 ring-current/20` : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                    }`}
                  >
                    <lt.Icon className={`h-5 w-5 ${isSelected ? lt.textColor : "text-slate-400"}`} />
                    <span className={`text-xs font-medium ${isSelected ? lt.textColor : "text-slate-600"}`}>{lt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conditional fields */}
          <AnimatePresence mode="wait">
            {transferTo === "firm_vault" && (
              <motion.div key="vault" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vault Reference</label>
                  <input value={transferFields.vaultReference || ""} onChange={e => setTransferFields({ ...transferFields, vaultReference: e.target.value })}
                    placeholder="e.g., V-2024-042" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Shelf Location</label>
                  <input value={transferFields.shelfLocation || ""} onChange={e => setTransferFields({ ...transferFields, shelfLocation: e.target.value })}
                    placeholder="e.g., Shelf B3, Row 2" className={inputClass} />
                </div>
              </motion.div>
            )}
            {transferTo === "client_possession" && (
              <motion.div key="client" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Storage Method</label>
                  <SearchableSelect options={STORAGE_METHODS} value={transferFields.storageMethod || null}
                    onChange={v => setTransferFields({ ...transferFields, storageMethod: v })} placeholder="Select storage method" clearable />
                </div>
                {transferFields.storageMethod === "bank_safe_deposit" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Institution</label>
                      <input value={transferFields.institution || ""} onChange={e => setTransferFields({ ...transferFields, institution: e.target.value })}
                        placeholder="e.g., TD Bank" className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Box Number</label>
                      <input value={transferFields.boxNumber || ""} onChange={e => setTransferFields({ ...transferFields, boxNumber: e.target.value })}
                        placeholder="e.g., 1234" className={inputClass} />
                    </div>
                  </div>
                )}
              </motion.div>
            )}
            {transferTo === "court_filing" && (
              <motion.div key="court" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Court Name</label>
                  <input value={transferFields.courtName || ""} onChange={e => setTransferFields({ ...transferFields, courtName: e.target.value })}
                    placeholder="e.g., Superior Court" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Filing Number</label>
                  <input value={transferFields.filingNumber || ""} onChange={e => setTransferFields({ ...transferFields, filingNumber: e.target.value })}
                    placeholder="e.g., CF-2024-001" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Filing Date</label>
                  <input type="date" value={transferFields.filingDate || ""} onChange={e => setTransferFields({ ...transferFields, filingDate: e.target.value })}
                    className={inputClass} />
                </div>
              </motion.div>
            )}
            {transferTo === "other_attorney" && (
              <motion.div key="attorney" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Firm Name</label>
                  <input value={transferFields.firmName || ""} onChange={e => setTransferFields({ ...transferFields, firmName: e.target.value })}
                    placeholder="e.g., Smith & Associates" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Info</label>
                  <input value={transferFields.contactInfo || ""} onChange={e => setTransferFields({ ...transferFields, contactInfo: e.target.value })}
                    placeholder="Phone or email" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">External Reference</label>
                  <input value={transferFields.externalReference || ""} onChange={e => setTransferFields({ ...transferFields, externalReference: e.target.value })}
                    placeholder="Their file #" className={inputClass} />
                </div>
              </motion.div>
            )}
            {transferTo === "destroyed_revoked" && (
              <motion.div key="destroyed" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Destruction Date</label>
                  <input type="date" value={transferFields.destructionDate || ""} onChange={e => setTransferFields({ ...transferFields, destructionDate: e.target.value })}
                    className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Destruction Method</label>
                  <input value={transferFields.destructionMethod || ""} onChange={e => setTransferFields({ ...transferFields, destructionMethod: e.target.value })}
                    placeholder="e.g., Shredded, Burned" className={inputClass} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Reason */}
          {transferTo && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reason for Transfer</label>
              <textarea value={transferReason} onChange={e => setTransferReason(e.target.value)} rows={2}
                placeholder="Describe why the document is being transferred..."
                className={inputClass}
              />
            </motion.div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => { setShowTransfer(false); setTransferTo(null); setTransferFields({}); setTransferReason(""); }}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
            <button onClick={handleTransfer} disabled={!transferTo || saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Transferring..." : "Transfer Custody"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Add Copy Modal ─────────────────────────────────────────────────── */}
      <PremiumModal
        open={showAddCopy}
        onClose={() => { setShowAddCopy(false); setCopyForm({ ...emptyCopy }); }}
        title="Add Copy Record"
        subtitle="Track a distributed copy of the document"
        icon={<Copy className="h-5 w-5" />}
        size="md"
      >
        <div className="p-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Name *</label>
            <input value={copyForm.recipientName} onChange={e => setCopyForm({ ...copyForm, recipientName: e.target.value })}
              placeholder="Full name" className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
              <SearchableSelect options={COPY_ROLES} value={copyForm.recipientRole || null}
                onChange={v => setCopyForm({ ...copyForm, recipientRole: v || "" })} placeholder="Select role" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date Provided</label>
              <input type="date" value={copyForm.dateProvided} onChange={e => setCopyForm({ ...copyForm, dateProvided: e.target.value })}
                className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
              <SearchableSelect options={COPY_FORMATS} value={copyForm.format || null}
                onChange={v => setCopyForm({ ...copyForm, format: v || "physical" })} placeholder="Select format" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Acknowledgment</label>
              <SearchableSelect options={ACK_STATUSES} value={copyForm.acknowledged || null}
                onChange={v => setCopyForm({ ...copyForm, acknowledged: v || "not_requested" })} placeholder="Select status" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={copyForm.notes || ""} onChange={e => setCopyForm({ ...copyForm, notes: e.target.value })} rows={2}
              placeholder="Optional notes..." className={inputClass} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => { setShowAddCopy(false); setCopyForm({ ...emptyCopy }); }}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
            <button onClick={handleAddCopy} disabled={!copyForm.recipientName || !copyForm.recipientRole}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >Add Copy</button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Complete Review Modal ───────────────────────────────────────────── */}
      <PremiumModal
        open={showReview}
        onClose={() => { setShowReview(false); setReviewForm({ ...emptyReview }); }}
        title="Complete Review"
        subtitle="Record a document review"
        icon={<Eye className="h-5 w-5" />}
        size="md"
      >
        <div className="p-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Review Date *</label>
            <input type="date" value={reviewForm.date} onChange={e => setReviewForm({ ...reviewForm, date: e.target.value })}
              className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Outcome</label>
            <SearchableSelect
              options={[
                { value: "No Changes Needed", label: "No Changes Needed" },
                { value: "Minor Updates Required", label: "Minor Updates Required" },
                { value: "Major Revision Needed", label: "Major Revision Needed" },
                { value: "Complete Rewrite Recommended", label: "Complete Rewrite Recommended" },
              ]}
              value={reviewForm.outcome || null}
              onChange={v => setReviewForm({ ...reviewForm, outcome: v || "" })}
              placeholder="Select outcome"
              clearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={reviewForm.notes || ""} onChange={e => setReviewForm({ ...reviewForm, notes: e.target.value })} rows={3}
              placeholder="Review notes and observations..." className={inputClass} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => { setShowReview(false); setReviewForm({ ...emptyReview }); }}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
            <button onClick={handleAddReview} disabled={!reviewForm.date}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >Complete Review</button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Add Life Event Modal ────────────────────────────────────────────── */}
      <PremiumModal
        open={showLifeEvent}
        onClose={() => { setShowLifeEvent(false); setLifeEventForm({ ...emptyLifeEvent }); }}
        title="Add Life Event"
        subtitle="Record an event that may trigger a will review"
        icon={<Heart className="h-5 w-5" />}
        size="md"
      >
        <div className="p-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Event Type *</label>
            <div className="grid grid-cols-5 gap-2">
              {LIFE_EVENT_TYPES.map(et => {
                const isSelected = lifeEventForm.eventType === et.value;
                return (
                  <button key={et.value} onClick={() => setLifeEventForm({ ...lifeEventForm, eventType: et.value })}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all text-center ${
                      isSelected ? "border-pink-400 bg-pink-50 ring-2 ring-pink-400/20" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <et.Icon className={`h-4 w-4 ${isSelected ? "text-pink-600" : "text-slate-400"}`} />
                    <span className={`text-[10px] font-medium leading-tight ${isSelected ? "text-pink-700" : "text-slate-500"}`}>{et.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Event Date *</label>
            <input type="date" value={lifeEventForm.eventDate} onChange={e => setLifeEventForm({ ...lifeEventForm, eventDate: e.target.value })}
              className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea value={lifeEventForm.description || ""} onChange={e => setLifeEventForm({ ...lifeEventForm, description: e.target.value })} rows={2}
              placeholder="Describe the event..." className={inputClass} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={lifeEventForm.triggeredReview}
              onChange={e => setLifeEventForm({ ...lifeEventForm, triggeredReview: e.target.checked })}
              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            This event triggered a will review
          </label>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => { setShowLifeEvent(false); setLifeEventForm({ ...emptyLifeEvent }); }}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
            <button onClick={handleAddLifeEvent} disabled={!lifeEventForm.eventType || !lifeEventForm.eventDate}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >Add Event</button>
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default EstateCustodyTab;
